import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MovieIcon from '@mui/icons-material/Movie';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { createAssetExplorerApi, fileToBase64, getAssetTypeFromMime, isAssetPublished } from 'components/AssetExplorer/AssetExplorer.api';
import { Asset, AssetType } from 'components/AssetExplorer/AssetExplorer.types';
import { reelMakerApi } from 'components/ReelMaker/ReelMaker.api';
import { Reel } from 'components/ReelMaker/ReelMaker.types';
import { formatDuration } from '../playlists/Playlists.types';
import { screenDialogBodySx } from '../screens/dialogSx';
import { BEAM_ON_SCREEN_ASSETS_API_BASE_PATH } from '../Assets';
import { onDemandApi } from './OnDemand.api';
import { OnDemandKind } from './OnDemand.types';

const STEPS = ['Choose', 'Content', 'Schedule', 'Done'];
const assetsApi = createAssetExplorerApi(BEAM_ON_SCREEN_ASSETS_API_BASE_PATH);

interface DraftItem {
    key: string;
    name: string;
    durationMs: number;
    assetId?: string;
    mimeType?: string;
    reelId?: string;
    version?: number;
}

const readDurationMs = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const media = document.createElement(file.type.startsWith('audio/') ? 'audio' : 'video');
        const finish = (ms: number, error?: Error) => {
            URL.revokeObjectURL(url);
            if (error) reject(error);
            else resolve(ms);
        };
        media.preload = 'metadata';
        media.onloadedmetadata = () => {
            const seconds = media.duration;
            finish(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : 0);
        };
        media.onerror = () => finish(0, new Error(`Could not read the length of ${file.name}`));
        media.src = url;
    });

const moveItem = (items: DraftItem[], from: number, to: number): DraftItem[] => {
    if (to < 0 || to >= items.length || from === to) return items;
    const copy = [...items];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
};

interface OnDemandWizardProps {
    screenId: string;
    onScheduled: () => void;
}

/** Panel 1: choose Video, Audio or Reels, then schedule the playlist onto this screen. */
export const OnDemandWizard: React.FC<OnDemandWizardProps> = ({ screenId, onScheduled }) => {
    const [step, setStep] = useState(0);
    const [kind, setKind] = useState<OnDemandKind | null>(null);
    const [items, setItems] = useState<DraftItem[]>([]);
    const [name, setName] = useState('');
    const [maxPlays, setMaxPlays] = useState(1);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [assetPickerOpen, setAssetPickerOpen] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();

    const totalMs = useMemo(() => items.reduce((sum, item) => sum + item.durationMs, 0), [items]);

    const reset = () => {
        setStep(0);
        setKind(null);
        setItems([]);
        setName('');
        setMaxPlays(1);
        setError(null);
        setProgress('');
    };

    const choose = (next: OnDemandKind) => {
        setKind(next);
        setItems([]);
        setError(null);
        setStep(1);
    };

    const uploadFiles = async (list: FileList | null) => {
        const files = Array.from(list || []);
        if (!files.length || !kind || kind === 'reels') return;
        setBusy(true);
        setError(null);
        try {
            const folderId = await onDemandApi.uploadFolderId();
            const added: DraftItem[] = [];
            for (let index = 0; index < files.length; index += 1) {
                const file = files[index];
                setProgress(`Uploading ${index + 1} of ${files.length}: ${file.name}`);
                const assetType = getAssetTypeFromMime(file.type || '');
                const expected: AssetType = kind === 'audio' ? 'AUDIO' : 'VIDEO';
                if (assetType !== expected) {
                    throw new Error(`${file.name} is not a ${kind} file`);
                }
                const durationMs = await readDurationMs(file);
                if (durationMs < 1) throw new Error(`${file.name} has no play time`);
                const asset = await assetsApi.uploadAsset({
                    folderId,
                    name: file.name,
                    type: expected,
                    assetData: await fileToBase64(file),
                    mimeType: file.type || (expected === 'AUDIO' ? 'audio/mpeg' : 'video/mp4'),
                    originalName: file.name,
                    size: file.size,
                    metadata: { duration: durationMs / 1000 },
                });
                added.push({
                    key: asset._id,
                    name: asset.name || file.name,
                    durationMs,
                    assetId: asset._id,
                    mimeType: asset.mimeType,
                });
            }
            setItems((current) => [...current, ...added]);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
            setBusy(false);
            setProgress('');
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const addReels = async (picked: Reel[]) => {
        setPickerOpen(false);
        if (!picked.length) return;
        setBusy(true);
        setError(null);
        try {
            const added = await Promise.all(
                picked.map(async (reel) => {
                    const snapshot = await reelMakerApi.getReelVersion(reel._id, reel.publishedVersion);
                    const durationMs = Number((snapshot.canvas as { durationMs?: number } | null)?.durationMs) || 0;
                    if (durationMs < 1) throw new Error(`${reel.name} has no play time`);
                    return {
                        key: `${reel._id}:${reel.publishedVersion}`,
                        name: reel.name,
                        durationMs,
                        reelId: reel._id,
                        version: reel.publishedVersion,
                    } satisfies DraftItem;
                }),
            );
            setItems((current) => [...current, ...added.filter((item) => !current.some((existing) => existing.key === item.key))]);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not add reels');
        } finally {
            setBusy(false);
        }
    };

    const addAssets = (picked: Asset[]) => {
        setAssetPickerOpen(false);
        const added: DraftItem[] = [];
        for (const asset of picked) {
            if (!isAssetPublished(asset)) continue;
            const durationMs = Math.round(Number(asset.metadata?.duration || 0) * 1000);
            if (durationMs < 1) {
                setError(`${asset.name} has no play time`);
                continue;
            }
            added.push({
                key: asset._id,
                name: asset.name,
                durationMs,
                assetId: asset._id,
                mimeType: asset.mimeType,
            });
        }
        setItems((current) => [...current, ...added.filter((item) => !current.some((existing) => existing.key === item.key))]);
    };

    const schedule = async () => {
        if (!kind) return;
        const playlistName = name.trim();
        if (!playlistName) {
            setError('Playlist name is required');
            return;
        }
        if (!items.length) {
            setError('Add at least one item');
            return;
        }
        const plays = Math.min(20, Math.max(1, Math.round(maxPlays) || 1));
        setBusy(true);
        setError(null);
        try {
            await onDemandApi.create(screenId, {
                name: playlistName,
                kind,
                maxPlays: plays,
                items: items.map((item) =>
                    kind === 'reels'
                        ? { type: 'reel' as const, reelId: item.reelId, version: item.version, name: item.name, durationMs: item.durationMs }
                        : { type: kind, assetId: item.assetId, name: item.name, mimeType: item.mimeType, durationMs: item.durationMs },
                ),
            });
            setMaxPlays(plays);
            setStep(3);
            onScheduled();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not schedule the playlist');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Schedule a playlist</Typography>
            <Stepper
                activeStep={step}
                alternativeLabel
                sx={{
                    mb: 3,
                    '& .MuiStepIcon-root': { color: theme.palette.mode === 'dark' ? '#90a4ae' : '#78909c' },
                    '& .MuiStepIcon-root.Mui-active': { color: theme.palette.secondary.main },
                    '& .MuiStepIcon-root.Mui-completed': { color: theme.palette.success.main },
                    '& .MuiStepIcon-text': { fill: '#fff' },
                    '& .MuiStepLabel-label, & .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
                        color: theme.palette.text.primary,
                    },
                }}
            >
                {STEPS.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
            </Stepper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {step === 0 && (
                <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">What should this screen play?</Typography>
                    <Button variant="contained" color="secondary" startIcon={<MovieIcon />} onClick={() => choose('video')}>Play Video</Button>
                    <Button variant="contained" color="secondary" startIcon={<AudiotrackIcon />} onClick={() => choose('audio')}>Play Audio</Button>
                    <Button variant="contained" color="secondary" startIcon={<SlideshowIcon />} onClick={() => choose('reels')}>Play Reels</Button>
                </Stack>
            )}

            {step === 1 && kind && (
                <Stack spacing={1.5}>
                    {kind === 'reels' ? (
                        <Button variant="outlined" color="secondary" onClick={() => setPickerOpen(true)} disabled={busy}>Add published reels</Button>
                    ) : (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                hidden
                                multiple
                                accept={kind === 'audio' ? 'audio/*' : 'video/*'}
                                onChange={(e) => void uploadFiles(e.target.files)}
                            />
                            <Button variant="outlined" color="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
                                {kind === 'audio' ? 'Choose audio files' : 'Choose video files'}
                            </Button>
                            <Button variant="outlined" color="secondary" disabled={busy} onClick={() => setAssetPickerOpen(true)}>
                                {kind === 'audio' ? 'Choose audio from assets' : 'Choose video from assets'}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Upload new files, or pick published video and audio from Beam on Screen assets. An unpublished asset can be published before it is added.
                            </Typography>
                        </>
                    )}
                    {progress && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} color="secondary" />
                            <Typography variant="body2">{progress}</Typography>
                        </Box>
                    )}
                    {items.length === 0 && !busy && <Alert severity="info">Nothing added yet.</Alert>}
                    {items.map((item, index) => (
                        <Box key={item.key} display="flex" alignItems="center" gap={1}>
                            <Box flex={1} minWidth={0}>
                                <Typography variant="body2" noWrap>{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{formatDuration(item.durationMs)}</Typography>
                            </Box>
                            <Tooltip title="Move up">
                                <span>
                                    <IconButton size="small" aria-label={`Move ${item.name} up`} disabled={index === 0} onClick={() => setItems((list) => moveItem(list, index, index - 1))}>
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                                <span>
                                    <IconButton size="small" aria-label={`Move ${item.name} down`} disabled={index === items.length - 1} onClick={() => setItems((list) => moveItem(list, index, index + 1))}>
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Remove">
                                <IconButton size="small" aria-label={`Remove ${item.name}`} onClick={() => setItems((list) => list.filter((entry) => entry.key !== item.key))}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                    <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button variant="outlined" color="secondary" onClick={() => setStep(0)} disabled={busy}>Back</Button>
                        <Button variant="contained" color="secondary" disabled={busy || items.length === 0} onClick={() => setStep(2)}>Next</Button>
                    </Box>
                </Stack>
            )}

            {step === 2 && (
                <Stack spacing={2}>
                    <TextField label="Playlist name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth required />
                    <Typography variant="body2">Total play time: {formatDuration(totalMs)}</Typography>
                    <TextField
                        label="Times to play"
                        type="number"
                        size="small"
                        value={maxPlays}
                        onChange={(e) => setMaxPlays(Number(e.target.value))}
                        inputProps={{ min: 1, max: 20, step: 1 }}
                        helperText="From 1 to 20. The default is 1."
                        sx={{ maxWidth: 220 }}
                    />
                    <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button variant="outlined" color="secondary" onClick={() => setStep(1)} disabled={busy}>Back</Button>
                        <Button variant="contained" color="success" disabled={busy || !name.trim()} onClick={() => void schedule()}>
                            {busy ? 'Scheduling…' : 'Schedule to Play'}
                        </Button>
                    </Box>
                </Stack>
            )}

            {step === 3 && (
                <Stack spacing={2}>
                    <Alert severity="success">
                        {name.trim() || 'Playlist'} is queued. It can play {maxPlays} time{maxPlays === 1 ? '' : 's'} ({formatDuration(totalMs)} each time).
                    </Alert>
                    <Button variant="contained" color="secondary" onClick={reset}>Start another</Button>
                </Stack>
            )}

            <PublishedReelPicker
                open={pickerOpen}
                excludeKeys={items.map((item) => item.key)}
                onClose={() => setPickerOpen(false)}
                onPick={(picked) => void addReels(picked)}
            />
            {kind && kind !== 'reels' && (
                <PublishedAssetPicker
                    open={assetPickerOpen}
                    kind={kind}
                    excludeKeys={items.map((item) => item.key)}
                    onClose={() => setAssetPickerOpen(false)}
                    onPick={addAssets}
                />
            )}
        </Paper>
    );
};

interface PublishedReelPickerProps {
    open: boolean;
    excludeKeys: string[];
    onClose: () => void;
    onPick: (picked: Reel[]) => void;
}

/** Published reels only. The current published version of each selection is added. */
const PublishedReelPicker: React.FC<PublishedReelPickerProps> = ({ open, excludeKeys, onClose, onPick }) => {
    const [reels, setReels] = useState<Reel[]>([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const excluded = excludeKeys.join('|');
    useEffect(() => {
        if (!open) return;
        setSelected([]);
        setSearch('');
        setLoading(true);
        setError(null);
        const skip = new Set(excluded.split('|').filter(Boolean));
        reelMakerApi
            .listReels({ status: 'published', sortBy: 'name', sortOrder: 'asc' })
            .then((list) => setReels(list.filter((reel) => reel.publishedVersion > 0 && !skip.has(`${reel._id}:${reel.publishedVersion}`))))
            .catch((err) => setError(err instanceof Error ? err.message : 'Could not load reels'))
            .finally(() => setLoading(false));
    }, [open, excluded]);

    const visible = reels.filter((reel) => reel.name.toLowerCase().includes(search.trim().toLowerCase()));
    const toggle = (reelId: string) => setSelected((current) => (current.includes(reelId) ? current.filter((id) => id !== reelId) : [...current, reelId]));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add published reels</DialogTitle>
            <DialogContent dividers sx={screenDialogBodySx}>
                <TextField size="small" fullWidth label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }} />
                {error && <Alert severity="error">{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress color="secondary" /></Box>
                ) : visible.length === 0 ? (
                    <Alert severity="info">No published reels found. Publish a reel in Reel Maker first.</Alert>
                ) : (
                    <List dense>
                        {visible.map((reel) => (
                            <ListItem key={reel._id} disablePadding>
                                <ListItemButton onClick={() => toggle(reel._id)}>
                                    <ListItemIcon>
                                        <Checkbox edge="start" checked={selected.includes(reel._id)} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary={reel.name} secondary={`${reel.publishedLabel ?? ''} · ${reel.width}×${reel.height}`} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={selected.length === 0 || loading}
                    onClick={() => onPick(reels.filter((reel) => selected.includes(reel._id)))}
                >
                    Add{selected.length ? ` ${selected.length}` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface PublishedAssetPickerProps {
    open: boolean;
    kind: 'video' | 'audio';
    excludeKeys: string[];
    onClose: () => void;
    onPick: (picked: Asset[]) => void;
}

/** Video or audio from Beam on Screen assets. Unpublished files can be published before they are queued. */
const PublishedAssetPicker: React.FC<PublishedAssetPickerProps> = ({ open, kind, excludeKeys, onClose, onPick }) => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [publishingId, setPublishingId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const expected: AssetType = kind === 'audio' ? 'AUDIO' : 'VIDEO';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const page = await assetsApi.getAssets(undefined, 0, 200, 'name', 'asc', false);
            const skip = new Set(excludeKeys);
            setAssets((page.assets || []).filter((asset) => asset.type === expected && !skip.has(asset._id)));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load assets');
        } finally {
            setLoading(false);
        }
    }, [excludeKeys, expected]);

    useEffect(() => {
        if (!open) return;
        setSelected([]);
        setSearch('');
        void load();
    }, [open, load]);

    const publish = async (asset: Asset) => {
        setPublishingId(asset._id);
        setError(null);
        try {
            await assetsApi.publishExplorerItems({ assetIds: [asset._id], folderIds: [] });
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not publish the asset');
        } finally {
            setPublishingId('');
        }
    };

    const visible = assets.filter((asset) => asset.name.toLowerCase().includes(search.trim().toLowerCase()));
    const toggle = (assetId: string) => setSelected((current) => (current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{kind === 'audio' ? 'Choose audio from assets' : 'Choose video from assets'}</DialogTitle>
            <DialogContent dividers sx={screenDialogBodySx}>
                <TextField size="small" fullWidth label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }} />
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress color="secondary" /></Box>
                ) : visible.length === 0 ? (
                    <Alert severity="info">No {kind} assets found in Beam on Screen assets.</Alert>
                ) : (
                    <List dense>
                        {visible.map((asset) => {
                            const published = isAssetPublished(asset);
                            return (
                                <ListItem
                                    key={asset._id}
                                    disablePadding
                                    secondaryAction={!published ? (
                                        <Button size="small" variant="outlined" color="secondary" disabled={publishingId === asset._id} onClick={() => void publish(asset)}>
                                            {publishingId === asset._id ? 'Publishing…' : 'Publish'}
                                        </Button>
                                    ) : undefined}
                                >
                                    <ListItemButton disabled={!published} onClick={() => toggle(asset._id)}>
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={selected.includes(asset._id)} tabIndex={-1} disableRipple disabled={!published} />
                                        </ListItemIcon>
                                        <ListItemText primary={asset.name} secondary={published ? 'Published' : 'Not published'} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={selected.length === 0 || loading}
                    onClick={() => onPick(assets.filter((asset) => selected.includes(asset._id)))}
                >
                    Add{selected.length ? ` ${selected.length}` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
